/* eslint-disable react/prop-types */
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { FC, useContext, useState } from "react";
import {
  Controller,
  ControllerRenderProps,
  FieldValues,
  useFormContext,
  useWatch,
} from "react-hook-form";
import {
  AppContextType,
  StateContext,
  StateContextType,
} from "../../context/ApplicationContext";
import { funcExecutor } from "../../functions/generic-functions";
import { evaluateExpression } from "../../functions/parser";
import { useAppSelector } from "../../store/hooks";
import {
  ExtendedEvents,
  FunctionDefinition,
  NameValueTuple,
} from "../model/form-configuration";
import HelperText from "./HelperText";
import { PPRadioGroupProps } from "./PPField";
import { cloneDeep, isEqual } from "lodash";

export interface ChoiceValue {
  label: string;
  value: string;
}

export type SourceTransitionalState = {
  previousValue: string | undefined;
  currentValue: string | undefined;
};

type InnerRadioFieldProps = {
  props: PPRadioGroupProps;
  field: ControllerRenderProps<FieldValues, string>;
};

const InnerRadioField = ({ props, field }: InnerRadioFieldProps) => {
  const { control, getValues, setValue } = useFormContext();
  const stateCtx: StateContextType = useContext(StateContext);

  const requiredTokens: string[] = props.extendedAttributes["required"]
    ? props.extendedAttributes["required"].split(" ")
    : [];
  const watch: FieldValues = useWatch({ control, defaultValue: undefined });
  const isRequired: boolean = evaluateExpression(requiredTokens, watch);

  const appContext = stateCtx.applicationContext;
  const setAppContext = stateCtx.setApplicationContext;

  const getParameter = (nameValue: NameValueTuple): any => {
    let param: any = undefined;
    switch (nameValue.type) {
      case "string":
        param = getString(nameValue);
        break;
      case "stringArray":
        param = getStringArray(nameValue);
        break;
      case "rpn":
        param = getExpression(nameValue);
        break;
      default:
        param = nameValue.value;
    }
    return param;
  };

  const getString = (nameValue: NameValueTuple): string => nameValue.value;

  const getExpression = (nameValue: NameValueTuple): string => nameValue.value;

  const getStringArray = (nameValue: NameValueTuple): string[] => {
    const stringArrRegEx: RegExp = /\[(.*?)\]/;
    const stringVals: RegExpMatchArray | null =
      nameValue.value.match(stringArrRegEx);
    if (stringVals !== null) {
      return stringVals[1].split(",");
    } else return [] as string[];
  };

  const onChange = (
    appContext: AppContextType,
    choiceState: SourceTransitionalState
  ) => {
    const events: ExtendedEvents | undefined = props.extendedEvents;

    if (events?.onChange) {
      const functions: FunctionDefinition[] = events.onChange;
      const sortedFunctions: FunctionDefinition[] = functions
        .slice()
        .sort(
          (a: FunctionDefinition, b: FunctionDefinition) => a.order - b.order
        );

      sortedFunctions.forEach((func: FunctionDefinition) => {
        const sortedParams: NameValueTuple[] = func.parameters
          .slice()
          .sort((a: NameValueTuple, b: NameValueTuple) => a.order - b.order);

        funcExecutor(
          func.name,
          sortedParams.map((nameValue: NameValueTuple): any =>
            getParameter(nameValue)
          ),
          getValues,
          setValue,
          appContext,
          choiceState
        );
      });
    }
  };

  const isFieldInvalid: boolean =
    appContext.fieldState[field.name] !== undefined &&
    !appContext.fieldState[field.name];
  const isHideField: boolean =
    appContext.fieldHide[field.name] !== undefined &&
    appContext.fieldHide[field.name];
  const isDisabledField: boolean =
    appContext.fieldDisable[field.name] !== undefined &&
    appContext.fieldDisable[field.name];

  return (
    <FormControl
      component={`fieldset`}
      error={isFieldInvalid}
      sx={isHideField ? { display: "none" } : null}
    >
      <FormLabel id={props.id}>
        {props.label + (isRequired ? " *" : "")}
      </FormLabel>
      <RadioGroup
        {...field}
        row
        aria-labelledby={props.id}
        onChange={(_: React.ChangeEvent<HTMLInputElement>, value: string) => {
          field.onChange(value);
          const newAppContext: AppContextType = cloneDeep(appContext);
          newAppContext.fieldState[field.name] = isRequired && value != "";
          onChange(newAppContext, {
            currentValue: value,
            previousValue: field.value,
          });
          if (!isEqual(appContext, newAppContext)) setAppContext(newAppContext);
        }}
      >
        {props.choices.map((choice) => {
          return (
            <FormControlLabel
              key={choice.value}
              value={choice.value}
              control={<Radio size="small" />}
              label={choice.label}
            />
          );
        })}
      </RadioGroup>
      {
        <FormHelperText sx={isHideField ? { display: "none" } : null}>
          {isFieldInvalid ? (
            "Field is required"
          ) : (
            <HelperText helperText={props.helperText}></HelperText>
          )}
        </FormHelperText>
      }
    </FormControl>
  );
};

const PPRadioGroup: FC<PPRadioGroupProps> = (props) => {
  const { control } = useFormContext();
  return (
    <Controller
      name={props.id}
      control={control}
      defaultValue=""
      render={({ field }) => {
        return <InnerRadioField props={props} field={field} />;
      }}
    />
  );
};

export default PPRadioGroup;
//PPRadioGroup.whyDidYouRender = true;
